import { formatUsd } from "./money";
import type { ProposalInput, ProposalPlan } from "./types";
import { DUE_TEXT, GENERAL_CONDITIONS, PAYMENT_SCHEDULE_WORDING } from "./wording";

/**
 * The proposal's money: totals, depreciation split, deductible, insurance ACV payment,
 * out-of-pocket, and the payment schedule. Ported from the Worker's `computePlan()`
 * (src/insuranceProposal.ts) and the payment rows of `buildInsuranceProposalPdf()` and
 * `invoiceSpecFromProposal()` (src/docusign.ts). The Worker already summed in cents, so these
 * match it to the cent.
 */

export type PlanInput = Pick<ProposalInput, "scope" | "excluded" | "excludedRecoverable" | "deductibleCents" | "claimRecoverableDepCents" | "upgrades">;

/**
 * Worker `computePlan()`.
 * - Code-upgrade (paid when incurred) lines: their RCV is paid by the insurer after the work, so
 *   they're kept out of the ACV due, the deductible base, and the depreciation split.
 * - Deductible due is capped at the contract's ACV due; the insurer's ACV payment always
 *   subtracts the full deductible.
 * - Out of pocket = contract + upgrades − insurance ACV payment (whole claim, so ACV for items NMT
 *   isn't doing counts as money the customer has) − recoverable depreciation on NMT's work −
 *   code-upgrade RCV. Non-recoverable depreciation is the customer's.
 */
export function computePlan(input: PlanInput): ProposalPlan {
  let rcv = 0, acv = 0, rec = 0, nonrec = 0, pwi = 0, pwiAcv = 0;
  for (const g of input.scope) {
    for (const it of g.items) {
      const dep = it.rcvCents - it.acvCents;
      rcv += it.rcvCents;
      acv += it.acvCents;
      if (it.paidWhenIncurred) {
        pwi += it.rcvCents;
        pwiAcv += it.acvCents;
        continue;
      }
      if (it.recoverable ?? g.recoverable ?? true) rec += dep;
      else nonrec += dep;
    }
  }
  let xr = 0, xa = 0, xrec = 0, xpwiAcv = 0;
  for (const it of input.excluded ?? []) {
    xr += it.rcvCents;
    xa += it.acvCents;
    if (it.paidWhenIncurred) {
      xpwiAcv += it.acvCents;
      continue;
    }
    if (it.recoverable ?? input.excludedRecoverable ?? true) xrec += it.rcvCents - it.acvCents;
  }
  const fullDed = input.deductibleCents;
  const acvDue = acv - pwiAcv;
  const ded = Math.min(fullDed, acvDue);
  const insAcv = acv + xa - pwiAcv - xpwiAcv - fullDed;
  const upgrades = input.upgrades?.totalCents ?? 0;
  return {
    contractRcvCents: rcv,
    contractAcvCents: acv,
    contractDepCents: rcv - acv,
    contractRecDepCents: rec,
    contractNonRecDepCents: nonrec,
    deductibleCents: fullDed,
    deductibleDueCents: ded,
    acvCheckDueCents: acvDue - ded,
    contractAcvDueCents: acvDue,
    contractPwiCents: pwi,
    claimPwiAcvCents: pwiAcv + xpwiAcv,
    excludedRcvCents: xr,
    excludedAcvCents: xa,
    excludedRecDepCents: input.claimRecoverableDepCents != null ? Math.max(0, input.claimRecoverableDepCents - rec) : xrec,
    claimRcvCents: rcv + xr,
    claimAcvCents: acv + xa,
    claimDepCents: rcv + xr - acv - xa,
    insuranceAcvPaymentCents: insAcv,
    acvDifferenceCents: insAcv - (acvDue - ded),
    upgradesTotalCents: upgrades,
    grandTotalCents: rcv + upgrades,
    outOfPocketCents: rcv + upgrades - insAcv - rec - pwi,
  };
}

/** The out-of-pocket figure as shown to the customer: never below $0. */
export function displayedOutOfPocketCents(plan: ProposalPlan): number {
  return Math.max(0, plan.outOfPocketCents);
}

/** Whether the "Recoverable depreciation" row/footnote shows (the Worker shows it, even at $0, unless the contract has only non-recoverable or code-upgrade depreciation). */
export function showsRecoverableDepreciation(plan: ProposalPlan): boolean {
  return plan.contractRecDepCents > 0 || (plan.contractNonRecDepCents === 0 && plan.contractPwiCents === 0);
}

export type PaymentRowKey = "deductible" | "acv" | "nonRecoverable" | "recoverable" | "codeUpgrade" | "upgrades" | "supplements";

/** One row of the PDF's Payment Schedule (each gets a customer-initials box). */
export interface PaymentScheduleRow {
  key: PaymentRowKey;
  title: string;
  when: string;
  /** null = "As approved" (supplements). */
  amountCents: number | null;
}

/**
 * The PDF's Payment Schedule rows, in order (Worker `buildInsuranceProposalPdf`).
 * The numeric rows always add up to `plan.grandTotalCents` ("Total payments to NMT").
 */
export function paymentSchedule(plan: ProposalPlan, input?: Pick<ProposalInput, "upgrades">): PaymentScheduleRow[] {
  const w = PAYMENT_SCHEDULE_WORDING;
  const rows: PaymentScheduleRow[] = [
    { key: "deductible", ...w.deductible, amountCents: plan.deductibleDueCents },
    { key: "acv", ...w.acv, amountCents: plan.acvCheckDueCents },
  ];
  if (plan.contractNonRecDepCents > 0) rows.push({ key: "nonRecoverable", ...w.nonRecoverable, amountCents: plan.contractNonRecDepCents });
  if (showsRecoverableDepreciation(plan)) rows.push({ key: "recoverable", ...w.recoverable, amountCents: plan.contractRecDepCents });
  if (plan.contractPwiCents > 0) rows.push({ key: "codeUpgrade", ...w.codeUpgrade, amountCents: plan.contractPwiCents });
  if (plan.upgradesTotalCents > 0) rows.push({ key: "upgrades", title: w.upgrades.title, when: input?.upgrades?.due ?? w.upgrades.when, amountCents: plan.upgradesTotalCents });
  rows.push({ key: "supplements", ...w.supplements, amountCents: null });
  return rows;
}

/** When an invoice payment is due (Worker `DUE_TEXT` codes). */
export type PaymentDue = keyof typeof DUE_TEXT;

/** One progress-billing payment (Worker `invoiceSpecFromProposal().payments`). */
export interface InvoicePayment {
  label: string;
  amountCents: number;
  due: PaymentDue;
  /** 0 = insurance work, 1 = upgrades (not part of the claim). */
  section: 0 | 1;
}

/**
 * The payments the Worker put on the draft invoice after signing (deductible, ACV,
 * non-recoverable depreciation, recoverable depreciation, code upgrade, upgrades), $0 rows dropped.
 * In the CRM these drive progress invoicing; they are not invoiced automatically on signing.
 */
export function invoicePayments(plan: ProposalPlan): InvoicePayment[] {
  const pays: InvoicePayment[] = [
    { label: "Insurance Deductible", amountCents: plan.deductibleDueCents, due: "R", section: 0 },
    { label: "ACV for Our Work", amountCents: plan.acvCheckDueCents, due: "A", section: 0 },
  ];
  if (plan.contractNonRecDepCents > 0) pays.push({ label: "Non-recoverable Depreciation", amountCents: plan.contractNonRecDepCents, due: "C", section: 0 });
  pays.push({ label: "Recoverable Depreciation", amountCents: plan.contractRecDepCents, due: "I", section: 0 });
  if (plan.contractPwiCents > 0) pays.push({ label: "Ordinance or Law (Code Upgrade)", amountCents: plan.contractPwiCents, due: "O", section: 0 });
  if (plan.upgradesTotalCents > 0) pays.push({ label: "Upgrades", amountCents: plan.upgradesTotalCents, due: "U", section: 1 });
  return pays.filter((x) => x.amountCents > 0);
}

// ---------- Page 1: "Your claim at a glance"

export interface LedgerRow {
  label: string;
  amountCents: number;
  /** Printed with a leading "–" and muted. */
  neg?: boolean;
  /** Bold, with a rule above. */
  total?: boolean;
  /** A small line under it, e.g. "(Total paid by insurance to date.)". */
  note?: string;
}

export interface ClaimAtAGlance {
  /** Box 1, "WHAT YOUR INSURANCE APPROVED". */
  insuranceApproved: LedgerRow[];
  /** Box 2, "HOW OUR CONTRACT IS PAID". */
  contractPaid: LedgerRow[];
  /** The three cells under the boxes: expected insurance total to you − NMT contract = difference. */
  cells: { label: string; amountCents: number; caption: string }[];
  /** The difference (contract − insurance expected): negative means insurance covers it all, shown in green as (amount). */
  difference: { amountCents: number; caption: string };
  /** The "* …" footnote under the cells. */
  footnote: string;
}

/**
 * The money on page 1 (Ryan, Oct 29): box 1 is the whole claim and every insurance dollar expected
 * (the ACV paid so far, then the recoverable depreciation and code upgrade on our work, released when
 * it's done); box 2 is how our contract is paid; under them, insurance expected − our contract = the
 * difference (the customer's out of pocket; negative when insurance covers it all).
 * `scopeNotes` decides the depreciation wording (an RPS note).
 */
export function claimAtAGlance(plan: ProposalPlan, scopeNotes: readonly string[] = []): ClaimAtAGlance {
  const rps = scopeNotes.some((n) => /RPS/.test(n));
  // The claim's recoverable depreciation: on our work, plus on items we're not doing.
  const claimRecDep = plan.contractRecDepCents + plan.excludedRecDepCents;
  const claimNonRecDep = plan.claimDepCents - claimRecDep;
  const laterCents = plan.contractRecDepCents + plan.contractPwiCents;
  const expectedCents = plan.insuranceAcvPaymentCents + laterCents;
  const insuranceApproved: LedgerRow[] = [
    { label: "Total approved (replacement cost)", amountCents: plan.claimRcvCents },
    { label: "Recoverable depreciation", amountCents: claimRecDep, neg: true },
    ...(claimNonRecDep !== 0 ? [{ label: rps ? "Non-recoverable depreciation & RPS portion" : "Non-recoverable depreciation", amountCents: claimNonRecDep, neg: true }] : []),
    { label: "Deductible", amountCents: plan.deductibleCents, neg: true },
    ...(plan.claimPwiAcvCents > 0 ? [{ label: "Code upgrade (paid when incurred)", amountCents: plan.claimPwiAcvCents, neg: true }] : []),
    { label: "Insurance total ACV", amountCents: plan.insuranceAcvPaymentCents, total: true, note: "(Total paid by insurance to date.)" },
    {
      label: "Expected additional insurance payments",
      amountCents: laterCents,
      note: plan.contractPwiCents > 0 ? "(Recoverable depreciation and code upgrade on contracted work.)" : "(Recoverable depreciation on contracted work.)",
    },
    { label: "Total insurance proceeds expected", amountCents: expectedCents, total: true },
  ];
  const contractPaid: LedgerRow[] = [
    { label: "Your deductible", amountCents: plan.deductibleDueCents },
    { label: "ACV for our work, less deductible", amountCents: plan.acvCheckDueCents },
    ...(plan.contractNonRecDepCents > 0 ? [{ label: "Non-recoverable depreciation", amountCents: plan.contractNonRecDepCents }] : []),
    ...(plan.contractRecDepCents > 0 || plan.contractNonRecDepCents === 0 ? [{ label: "Depreciation on our work*", amountCents: plan.contractRecDepCents }] : []),
    ...(plan.contractPwiCents > 0 ? [{ label: "Code upgrade (paid when incurred)*", amountCents: plan.contractPwiCents }] : []),
    ...(plan.upgradesTotalCents > 0 ? [{ label: "Upgrades (paid by you)", amountCents: plan.upgradesTotalCents }] : []),
    { label: "Contract total", amountCents: plan.grandTotalCents, total: true },
  ];
  // Contract − insurance expected: the same as the out-of-pocket estimate.
  const differenceCents = plan.grandTotalCents - expectedCents;
  const cells = [
    { label: "EXPECTED INSURANCE TOTAL TO YOU", amountCents: expectedCents, caption: "From box 1" },
    { label: "NMT CONTRACT", amountCents: plan.grandTotalCents, caption: "From box 2" },
  ];
  const difference = {
    amountCents: differenceCents,
    caption: differenceCents < 0 ? `Insurance funds exceed our contract by ${formatUsd(-differenceCents)}` : differenceCents === 0 ? "Insurance covers our contract" : "Your estimated out of pocket",
  };
  const exNote =
    plan.excludedRecDepCents > 0
      ? ` Depreciation on items not in this proposal (${formatUsd(plan.excludedRecDepCents)}) is not counted, because it is normally released only for work that is completed and invoiced; your insurer may still choose to release it.`
      : "";
  const starNotes: string[] = [];
  if (showsRecoverableDepreciation(plan)) starNotes.push("Your insurer releases recoverable depreciation on our work after it is completed and invoiced.");
  if (plan.contractPwiCents > 0) starNotes.push("Your insurer pays the code upgrade (Ordinance or Law) only after that work is completed and invoiced.");
  if (plan.contractNonRecDepCents > 0) starNotes.push("Non-recoverable depreciation is not paid by your insurer and is included in the difference.");
  const footnote = `* ${starNotes.join(" ")} Expected insurance payments assume those are released.${exNote}`;
  return { insuranceApproved, contractPaid, cells, difference, footnote };
}

// ---------- Validation and dates

/**
 * Problems that stop the Worker from building the PDF, with its messages; empty when fine.
 * Added for the CRM: a negative deductible is refused (no deductible discounts).
 */
export function proposalInputProblems(input: ProposalInput): string[] {
  const out: string[] = [];
  if (!input.customer?.name || !input.customer?.address) out.push("customer.name and customer.address are required.");
  if (!Number.isInteger(input.deductibleCents)) out.push("deductible must be a number.");
  else if (input.deductibleCents < 0) out.push("deductible can't be negative.");
  if (!input.scope?.length || input.scope.some((g) => !g.items?.length)) out.push("scope must be a non-empty array of groups, each with at least one line item.");
  for (const g of input.scope ?? []) {
    for (const it of g.items ?? []) {
      if (!Number.isInteger(it.rcvCents) || !Number.isInteger(it.acvCents)) out.push(`Line item "${it.description}" needs numeric rcv and acv.`);
      else if (it.acvCents > it.rcvCents) out.push(`Line item "${it.description}" has ACV greater than RCV.`);
    }
  }
  return out;
}

/** Days a proposal is valid (spec and Worker default). */
export const PROPOSAL_VALID_DAYS = 15;

/**
 * "Valid through" date: `date` ("October 13, 2026") plus `days`, formatted the same way.
 * undefined when the date can't be read (the Worker then leaves the row off).
 * Uses the calendar date as written, so the server's time zone doesn't matter.
 */
export function proposalValidThrough(date: string, days: number = PROPOSAL_VALID_DAYS): string | undefined {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return undefined;
  const u = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + days));
  return u.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

const NUM_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty", "twenty-one", "twenty-two", "twenty-three", "twenty-four", "twenty-five", "twenty-six", "twenty-seven", "twenty-eight", "twenty-nine", "thirty"];

/** "fifteen (15)" for the General Conditions acceptance window; digits alone past thirty. */
export function daysPhrase(n: number): string {
  const w = NUM_WORDS[n];
  return w ? `${w} (${n})` : String(n);
}

/** The General Conditions with the acceptance window filled in ("…within fifteen (15) days…"). */
export function generalConditions(validDays: number = PROPOSAL_VALID_DAYS): { title?: string; body: string }[] {
  return GENERAL_CONDITIONS.map((p, i) => (i === 0 ? { ...p, body: p.body.replace("{{DAYS}}", daysPhrase(validDays)) } : p));
}

/** `DUE_TEXT` wording for an invoice payment. */
export function invoiceDueText(due: PaymentDue): string {
  return DUE_TEXT[due];
}
